import { Popover as Primitive } from "radix-ui";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { cn } from "../../cn.ts";
import { popoverSurfaceClassName } from "../surfaces.ts";
import { useCloseOnOutsideScroll } from "../closeOnOutsideScroll.ts";
import { focusFirstMeaningfulControl } from "./openFocus.ts";
import { PortalContainerContext, usePortalContainer, usePortalHost, useStageBoundary } from "../portalContainer.tsx";

/**
 * FR-015-101, § Overlays, Popover, taken from shadcn/ui's Popover over Radix (Spectrum UI has
 * none) and themed through the variables of `packages/tokens` (2026-09-15, by the owner's
 * decision): placement, offset, `matchTriggerWidth`, flip and collision shift (Radix's
 * `avoidCollisions`), closing on outside interaction and on `Esc` without trapping focus (§ Focus).
 *
 * What differs from the source, and why: the source's fixed 288 px width (`w-72`) is not carried,
 * because Baton's popovers hold a filter builder, a refusal notice and a date field of different
 * widths and each sizes to its content; `matchTriggerWidth` sets the trigger's width instead. The
 * popover closes when the page under it scrolls (`useCloseOnOutsideScroll`), where the source follows
 * its trigger across the screen. A panel taller than the room the collision padding leaves it (the
 * display menu on a short viewport) is capped at Radix's available height and scrolls inside, so its
 * last control stays reachable; the source lets it run off the viewport. A popover may stand on a
 * rectangle rather than a trigger (`anchor`, 2026-09-30), Radix's virtual anchor, for a calendar block
 * it does not own, returning focus to that block on close as a trigger's popover does. A panel opened
 * inside the popover, such as a menu, portals into the popover's content and is not modal
 * (`portalContainer.tsx`, 2026-10-02), so the popover stays open through it and closes with it. Opening moves the focus to the panel's
 * first meaningful control, or to the panel when it holds none (`openFocus.ts`, 2026-10-02), where the
 * source takes the first tabbable element that is not a link, which could be a "⋯" icon button.
 */
export type Placement = "top" | "bottom" | "left" | "right";

/**
 * Where a popover with no trigger of its own stands (2026-09-30, 018 FR-018-300): the rectangle of
 * what was clicked, read when it opened, and the element focus returns to when it closes. A calendar
 * opens one on a block it draws, which is the grid's own button, not a trigger the popover owns.
 */
export interface PopoverAnchor {
  readonly rect: DOMRect;
  readonly returnFocus: HTMLElement | null;
}

export interface PopoverProps {
  /** The control that opens the popover; absent, `anchor` places it and `open` holds it. */
  readonly trigger?: ReactNode;
  readonly anchor?: PopoverAnchor;
  readonly placement?: Placement;
  readonly align?: "start" | "center" | "end";
  readonly offset?: number;
  readonly matchTriggerWidth?: boolean;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly label?: string;
  /** False for a popover that opens on its own, such as an inline refusal, so focus stays where the person is working. */
  readonly moveFocus?: boolean;
  /**
   * True while a control inside holds `Esc` for itself, such as a field whose typing `Esc` drops,
   * so the popover stays open. Radix reads `Esc` on the document in the capture phase, ahead of the
   * control's own handler, so the host says so here rather than stop the event (as `Dialog` does).
   */
  readonly escapeGuard?: () => boolean;
  /**
   * False while the popover closes because what was chosen in it opens another popover, which takes the
   * focus (2026-09-30, 018 FR-018-305: a meeting chosen in a day's list opens its preview): the focus is
   * left where that popover put it rather than handed back to the trigger, which would take it from the
   * new popover and so close it. True otherwise, the default.
   */
  readonly restoreFocus?: boolean;
  readonly children: ReactNode;
  readonly className?: string;
}

export function Popover({ trigger, anchor, placement = "bottom", align = "start", offset = 4, matchTriggerWidth = false, open, onOpenChange, label, moveFocus = true, escapeGuard, restoreFocus = true, children, className }: PopoverProps) {
  const container = usePortalContainer();
  const boundary = useStageBoundary();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const host = usePortalHost(panel, false);
  const panelElement = useCallback((element: HTMLDivElement | null) => {
    panelRef.current = element;
    setPanel(element);
  }, []);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const shown = open ?? uncontrolledOpen;
  const setShown = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  useCloseOnOutsideScroll(shown, panelRef, () => {
    setShown(false);
  });
  return (
    <Primitive.Root data-slot="popover" open={shown} onOpenChange={setShown}>
      {trigger !== undefined && (
        <Primitive.Trigger data-slot="popover-trigger" asChild>
          {trigger}
        </Primitive.Trigger>
      )}
      {anchor !== undefined && <Primitive.Anchor virtualRef={{ current: { getBoundingClientRect: () => anchor.rect } }} />}
      <Primitive.Portal {...(container === undefined ? {} : { container })}>
        <Primitive.Content
          ref={panelElement}
          data-slot="popover-content"
          {...boundary}
          className={cn(popoverSurfaceClassName, "max-h-(--radix-popover-content-available-height) origin-(--radix-popover-content-transform-origin) overflow-y-auto p-4 data-[match-width=true]:w-(--radix-popover-trigger-width)", className)}
          side={placement}
          align={align}
          sideOffset={offset}
          avoidCollisions
          collisionPadding={8}
          data-match-width={matchTriggerWidth ? "true" : undefined}
          onOpenAutoFocus={
            moveFocus
              ? focusFirstMeaningfulControl
              : (event: Event) => {
                  event.preventDefault();
                }
          }
          {...(anchor === undefined
            ? restoreFocus
              ? {}
              : {
                  onCloseAutoFocus: (event: Event) => {
                    event.preventDefault();
                  },
                }
            : {
                onCloseAutoFocus: (event: Event) => {
                  event.preventDefault();
                  if (restoreFocus && anchor.returnFocus?.isConnected === true) anchor.returnFocus.focus();
                },
              })}
          {...(escapeGuard === undefined ? {} : { onEscapeKeyDown: (event: KeyboardEvent) => { if (escapeGuard()) event.preventDefault(); } })}
          {...(label === undefined ? {} : { "aria-label": label })}
        >
          <PortalContainerContext.Provider value={host}>{children}</PortalContainerContext.Provider>
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}
